import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Loader2, Search, Mail, Download } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_LABELS = {
  homepage: 'Homepage',
  catalog: 'Catalog',
  summer_camps: 'Summer Camps',
  footer: 'Footer',
  division: 'Division Page',
};

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function exportCSV(subscribers) {
  const header = 'Email,First Name,Source,Subscribed On';
  const rows = subscribers.map(s =>
    [s.email, s.first_name || '', SOURCE_LABELS[s.source] || s.source || '', fmt(s.created_at)].join(','),
  );
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminNewsletterPage() {
  const { api } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/newsletter/subscribers/');
      setSubscribers(data.subscribers || []);
    } catch {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return subscribers;
    const needle = q.toLowerCase();
    return subscribers.filter(s =>
      (s.email || '').toLowerCase().includes(needle)
      || (s.first_name || '').toLowerCase().includes(needle),
    );
  }, [subscribers, q]);

  const sourceCounts = useMemo(() => {
    const counts = {};
    subscribers.forEach(s => {
      const label = SOURCE_LABELS[s.source] || s.source || 'Unknown';
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [subscribers]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6" /> Newsletter Subscribers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {subscribers.length} active subscribers.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => exportCSV(filtered)} disabled={loading || filtered.length === 0}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {sourceCounts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sourceCounts.map(([label, count]) => (
            <span key={label} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-slate-100 text-slate-700">
              {label} <span className="font-bold">{count}</span>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by email or name…"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="pl-8 max-w-sm"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">No subscribers found.</p>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Email</th>
                <th className="text-left px-4 py-2.5 font-medium">Name</th>
                <th className="text-left px-4 py-2.5 font-medium">Source</th>
                <th className="text-left px-4 py-2.5 font-medium">Subscribed</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(s => (
                <tr key={s.email} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">{s.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.first_name || '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-xs">
                      {SOURCE_LABELS[s.source] || s.source || 'Unknown'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmt(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
